import { useBookmarks } from "@circle-react-shared/Bookmarks/hooks/useBookmarks";
import { ShareToWebContent } from "@circle-react/components/Events/CompassEventsSpace/EventDetailPage/ShareToWeb/ShareToWebContent";
import { isPaidEvent } from "@circle-react/components/Events/CompassEventsSpace/helpers";
import { DeleteRecurringModal } from "@circle-react/components/Events/DeleteRecurringModal";
import { DuplicateEventModal } from "@circle-react/components/Events/ListViewItem/EventMenu/DuplicateEventModal";
import { useEventMenu } from "@circle-react/components/Events/ListViewItem/EventMenu/hooks/useEventMenu";
import { useEditEventModal } from "@circle-react/components/Modals/EditEventModal";
import { useCurrentPostHelpers } from "@circle-react/components/PostsV3/useCurrentPostHelpers";
import { BlockInViewOnlyMode } from "@circle-react/components/ViewOnlyMasquerading/BlockInViewOnlyMode";
import { useCurrentSpaceContext } from "@circle-react/contexts/currentSpaceContext";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { usePostSummary } from "@circle-react/hooks/posts/usePostSummary";
import { useSmScreenMediaQuery } from "@circle-react/hooks/useMediaQuery";
import { useBoolean } from "@circle-react/hooks/utils/useBoolean";
import type { Event } from "@circle-react/types/Event";
import type { Post } from "@circle-react/types/Post";
import type { IconButtonProps } from "@circleco/compass/components/IconButton";
import { IconButton } from "@circleco/compass/components/IconButton";
import { Menu } from "@circleco/compass/components/Menu";
import { Popover } from "@circleco/compass/components/Popover";
import { useRef } from "react";

import { t } from "@/i18n-js/instance";
import { TABS } from "@/react/components/Events/CompassEventModal/constants";
import type { RecurringEditMode } from "@/react/components/Events/helpers/constants";
import { useConvertEventToBroadcast } from "@/react/components/EventsV3/Modal/hooks/useConvertEventToBroadcast";
import { useIsMobileShellEnabled } from "@/react/components/Mobile/hooks/useIsMobileShellEnabled";
import { ConfirmationModal } from "@/react/components/SettingsApp/CompassCompat/ConfirmationModal";
import { isCommunityAdmin } from "@/react/helpers/communityMemberHelpers";
import { useCircleV4 } from "@/react/hooks/useCircleV4";

import { RemoveAttendeesFirstModal } from "./RemoveAttendeesFirstModal";
import { useDeleteEventMutation } from "./useDeleteEventMutation";
import { useEventDropdownMenuOptions } from "./useEventDropdownMenuOptions";

interface EventDropdownActionsComponentProps {
  displayPinOption?: boolean;
  displaySummarizeOption?: boolean;
  onChange: (data?: Post) => void;
  onPostDestroy: (...args: unknown[]) => void;
  size?: IconButtonProps["size"];
  variant?: IconButtonProps["variant"];
}

export const EventDropdownActionsComponent = ({
  displayPinOption = true,
  displaySummarizeOption = false,
  variant = "ghost",
  onChange,
  onPostDestroy,
  size = "sm",
}: EventDropdownActionsComponentProps) => {
  const {
    post: event,
    canUpdatePost,
    canDuplicatePost,
    canDestroyPost,
    isDeleteBlockedByPaidAttendees,
    canManagePost,
    refetchPostDetails,
    showShareOnWebOptions,
    getSharePostLink,
    sharePostData,
  } = useCurrentPostHelpers({ onChangeSharePost: onChange });

  const editEventModal = useEditEventModal();
  const { isCircleV4 } = useCircleV4();
  const isBlockedByPaidAttendees = isCircleV4 && isDeleteBlockedByPaidAttendees;
  const [isRemoveAttendeesModalOpen, , setIsRemoveAttendeesModalOpen] =
    useBoolean(false);
  const refreshBlocker = () => {
    refetchPostDetails?.();
  };

  const { refetch, data: space } = useCurrentSpaceContext();
  const isSmScreen = useSmScreenMediaQuery();
  const isMobileShellEnabled = useIsMobileShellEnabled();
  const [shouldShowDuplicateEventModal, toggleDuplicateEventModal] =
    useBoolean(false);
  const [isShareOpen, toggleShareOpen, setIsShareOpen] = useBoolean(false);
  const shareAnchorRef = useRef<HTMLButtonElement>(null);
  const {
    onDeleteClickHandler,
    onPinToTop,
    isConfirmationModalOpen,
    closeConfirmationModal,
    isRecurringModalOpen,
    toggleRecurringModal,
  } = useEventMenu({ event });
  const { currentCommunityMember } = usePunditUserContext();

  const isAdmin = isCommunityAdmin(currentCommunityMember);
  const isEventShareableViaBroadcast = isAdmin && event.status !== "draft";
  const isPinned = Boolean(event.pinned_at_top_of_space);
  const shouldShowShareInMenu =
    (isSmScreen || isMobileShellEnabled) && showShareOnWebOptions(space);

  const { onSendAnnouncement, isPending: isSendingAnnouncement } =
    useConvertEventToBroadcast();

  const { toggleSummary } = usePostSummary({ post: event });
  const canSummarize =
    displaySummarizeOption &&
    isMobileShellEnabled &&
    Boolean(event.has_summary);

  const { createBookmark, deleteBookmark } = useBookmarks({
    bookmarkId: event.bookmark_id,
    bookmarkType: "event",
    record: event,
    onSuccess: refetchPostDetails,
    disableFetch: true,
  });

  const handleBookmarkToggle = () => {
    event.bookmark_id ? deleteBookmark() : createBookmark();
  };

  const deleteMutation = useDeleteEventMutation({
    event: event as Event,
    onPostDestroy,
    onRefetch: refetch,
    onDeleteError: refreshBlocker,
  });

  const menuOptions = useEventDropdownMenuOptions({
    isBookmarked: Boolean(event.bookmark_id),
    onBookmarkToggle: handleBookmarkToggle,
    shouldShowShareInMenu,
    onShareClick: toggleShareOpen,
    canUpdatePost,
    onEditClick: () => {
      void editEventModal.show({ event, onClose: refreshBlocker });
    },
    canDuplicatePost,
    onDuplicateClick: toggleDuplicateEventModal,
    canManagePost,
    isPinned,
    onPinClick:
      !!space && displayPinOption
        ? () => {
            void onPinToTop();
          }
        : undefined,
    canDestroyPost,
    isDeleteBlockedByPaidAttendees: isBlockedByPaidAttendees,
    onDeleteClick: () => {
      if (isBlockedByPaidAttendees) {
        setIsRemoveAttendeesModalOpen(true);
        return;
      }
      onDeleteClickHandler();
    },
    isEventShareableViaBroadcast,
    onSendAnnouncementClick: () => onSendAnnouncement({ eventId: event.id }),
    isSendingAnnouncement,
    canSummarize,
    onSummarizeClick: toggleSummary,
  });

  const shouldDisplayDropdownActions =
    canUpdatePost ||
    canDuplicatePost ||
    canDestroyPost ||
    isBlockedByPaidAttendees ||
    canManagePost ||
    isMobileShellEnabled;

  if (!shouldDisplayDropdownActions) {
    return null;
  }

  return (
    <BlockInViewOnlyMode>
      <Menu
        options={menuOptions}
        trigger={
          <IconButton
            ref={shareAnchorRef}
            variant={variant}
            size={size}
            icon="dot-menu"
            aria-label={t("events.event_actions")}
            data-testid="action-more"
          />
        }
        align="end"
        side="bottom"
      />
      {/* Opened from the menu item. Anchor to the actions button instead of a fake trigger. */}
      {shouldShowShareInMenu && (
        <Popover
          anchor={shareAnchorRef}
          open={isShareOpen}
          onOpenChange={setIsShareOpen}
          side="bottom"
          align="end"
          className="w-[360px]"
        >
          <ShareToWebContent
            post={event}
            getSharePostLink={getSharePostLink}
            sharePostData={sharePostData}
          />
        </Popover>
      )}
      <RemoveAttendeesFirstModal
        isOpen={isRemoveAttendeesModalOpen}
        attendeeCount={event.paid_attendees_count ?? 0}
        eventName={event.display_title || event.name}
        onClose={() => setIsRemoveAttendeesModalOpen(false)}
        onGoToAttendees={() => {
          setIsRemoveAttendeesModalOpen(false);
          void editEventModal.show({
            event,
            defaultSection: TABS.PEOPLE,
            onClose: refreshBlocker,
          });
        }}
      />
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        title={t("event_delete_modal.confirm.title")}
        onClose={closeConfirmationModal}
        onCancel={closeConfirmationModal}
        onConfirm={() => deleteMutation.mutate("")}
        confirmVariant="danger"
        loading={deleteMutation.isPending}
        confirmText={t("delete_event")}
      >
        {t("event_delete_modal.confirm.description")}
      </ConfirmationModal>
      <DuplicateEventModal
        event={event}
        show={shouldShowDuplicateEventModal}
        onClose={toggleDuplicateEventModal}
      />
      {!isPaidEvent(event) && (
        <DeleteRecurringModal
          show={isRecurringModalOpen}
          isDeleting={deleteMutation.isPending}
          onConfirm={(frequency: RecurringEditMode) =>
            deleteMutation.mutate(frequency)
          }
          onCloseModal={toggleRecurringModal}
        />
      )}
    </BlockInViewOnlyMode>
  );
};
